import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Button } from '@neo/primitives/button'
import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { ArrowLeft } from 'lucide-react'
import { useRef } from 'react'
import { StepsBreadcrumbs } from 'v2/shared/components/steps-breadcrumbs'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast } from 'v2/utils/toasts'
import { z } from 'zod'
import { newKBFormDefaultValues } from '../knowledge-base/config'
import { DataSource } from '../knowledge-base/new/data-source'
import { PreviewSheet } from '../knowledge-base/new/data-source/preview-sheet'
import { Setup } from '../knowledge-base/new/document-setup'
import {
  type CreateKBPayload,
  type KBAppDocument,
  type KBDocument,
  KBDocumentSource,
  type NewKBFormValues,
} from '../knowledge-base/types'
import { useFlowStore } from '../store'
import { useLinkKnowledgeBaseMutation } from './mutations'

interface CreateKBInlineProps {
  onBack: () => void
  flowId: string
}

export const CreateKBInline = ({ onBack, flowId }: CreateKBInlineProps) => {
  const { mutate: linkKnowledgeBases } = useLinkKnowledgeBaseMutation()
  const agentSettings = useFlowStore((state) => state.agentSettings)
  // Opened from within an agent builder → pre-select the current agent in "Associated Agent Flows"
  // so the (always-applied) link to this agent is visible instead of silently added at submit.
  const defaultValues = useRef<NewKBFormValues>({ ...newKBFormDefaultValues, agents: [flowId] })

  const { mutate: createKB } = useMutation({
    mutationKey: flowQueryKeys().createKnowledgeBase,
    mutationFn: async (data: CreateKBPayload) => {
      const response = await flowService.createKnowledgeBase(data)
      return response.data
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to create knowledge base',
        description: `${getErrorText(error as AxiosError)}. Please try again later.`,
      })
    },
  })

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <Button
          variant="ghost"
          size="iconSm"
          onClick={onBack}
          data-testid="button-back-from-create-kb"
        >
          <ArrowLeft size={16} />
        </Button>
        <h2 className="text-h4-500">Create knowledge base</h2>
      </div>
      <GenericForm<NewKBFormValues>
        id="new-knowledge-base-form-inline"
        includeSubmitButton={false}
        className="min-h-0 flex-1"
        fields={[
          {
            name: 'step',
            type: 'custom',
            zodValidation: z.number().min(1).max(2),
            eventDataExtractor: (value) => value,
            className: 'flex flex-col min-h-0',
            component: ({ value, onChange, form }) => (
              <>
                <StepsBreadcrumbs
                  onNavigateStep={onChange}
                  currentStep={value as number}
                  steps={[
                    { number: 1, label: 'Setup' },
                    { number: 2, label: 'Data source' },
                  ]}
                />
                {value === 1 ? <Setup form={form} /> : null}
                {value === 2 ? <DataSource form={form} /> : null}
              </>
            ),
          },
        ]}
        onSubmit={(values) => {
          const isWebsite = values.type === KBDocumentSource.URL
          const isAll = values.url_selection_type === 'all'

          let documents: KBDocument[] | KBAppDocument[] = []

          switch (values.type) {
            case KBDocumentSource.APP:
              documents = values.app_data.map((article) => ({
                type: article.type,
                id: article.id,
              }))
              break
            case KBDocumentSource.URL:
              documents = values.links
                .filter((d) => (d as { selected: boolean }).selected)
                .map((d) => ({
                  display_name: null,
                  document_id: (d as { id: string }).id,
                  type: KBDocumentSource.URL,
                }))
              break
            case KBDocumentSource.FILE:
              documents = values.files.map((d) => ({
                display_name: (d as { file_name: string }).file_name,
                document_id: (d as { file_url: string }).file_url,
                type: KBDocumentSource.FILE,
              }))
              break
            default:
              documents = []
              break
          }

          createKB(
            {
              name: values.name,
              // Inline (agent-tab) create renders the SAME `Setup` step as the standalone flow, and
              // `Setup` reads `kbDocsModeEnabled` itself — so a flag-on org gets the Default/Docs
              // retrieval-mode dropdown here too (it is not hardcoded to the default in this path).
              // `values.profile` forwards whatever was picked; when the field is gated off it stays
              // at its schema default ('default', the voice-friendly choice for an agent KB).
              profile: values.profile,
              // Always keep this agent linked; dedupe in case it's still selected in the field.
              phlo_uuids: [...new Set([flowId, ...(values.agents || [])])],
              crawl_job_id: isWebsite ? values.jobId : undefined,
              include_image_content: !values.ignore_image_content,
              include_youtube_content: !values.ignore_youtube_content,
              sync_period: values.sync_frequency.sync_period,
              sync_frequency: values.sync_frequency.sync_frequency,
              sync_enabled: values.sync_enabled,
              source: {
                source_type: values.type,
                config: {
                  source_type: values.type,
                  url: values.url || '',
                  max_depth: values.max_depth,
                  max_pages: values.max_pages,
                  crawl_subpages: isAll ? true : values.crawl_subpages,
                  exclude_paths: values.exclude_paths,
                  include_paths: values.include_paths,
                  app_source: values.app_source || null,
                  app_source_id: values.app_source_id || null,
                  app_data: values.type === KBDocumentSource.APP ? documents : null,
                },
                documents: values.type === KBDocumentSource.APP ? undefined : documents,
              },
              url_selection_type: values.url_selection_type,
              type: values.type,
            },
            {
              onSuccess: (data) => {
                linkKnowledgeBases({
                  knowledgeBaseIds: [
                    ...(agentSettings?.knowledge_base_ids || []),
                    data.knowledge_base_id,
                  ],
                  flowId,
                })
                // The KB exists now, so leave the form even if the link fails (the mutation toasts
                // that): staying put invited a retry, and every retry created another KB.
                onBack()
              },
            },
          )
        }}
        defaultValues={defaultValues.current}
      />
      {/* Host the per-page crawl Preview sheet: the sub-page picker's Preview button calls
          openSheet(), which no-ops unless a #side-sheet container is present (the standalone
          /knowledge-base/new + detail flows get it from TableWithSheetLayout; this inline
          agent-tab flow renders its own). Without it, clicking Preview did nothing. */}
      <div id="side-sheet">
        <PreviewSheet />
      </div>
    </div>
  )
}
